import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { map, mergeMap, catchError, switchMap, filter } from 'rxjs/operators';
import { EnvService } from 'src/app/services/env.service';
import {
  GetStates,
  GetStatesSuccess,
  GetStatesFailed,
  StatesActions,
} from './states.actions';
import { selectLocale } from '../query-params';
import { getCurrentLabelsJson } from '../labels';
import { States } from '../countries';
import { getCountryConfig } from '../../config/country-config';
import { state } from '@angular/animations';
import { isSdfCardFlow } from 'src/app/config/app-config';

export interface CountryCMS {
  code: string;
  iso2code: string;
  name: string;
  states?: States[];
}

@Injectable()
export class StatesEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private envService: EnvService,
    private store: Store
  ) {}

  private statesUrl(endpoint: string): string {
    // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
    return endpoint === 'v3'
      ? `${this.envService.apiUrl}/v3/reference-data/countries/`
      : `${this.envService.apiUrl}/v2/reference-data/countries/`;
  }

  public getCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStates),
      // ToDo duplice states should not pull from API
      // Check store, if not exisit then fetch from API
      switchMap((action) =>
        combineLatest([
          of(action.getStatesRequest),
          of(action.endpoint),
          this.store.pipe(select(getCurrentLabelsJson)),
          this.store.pipe(select(selectLocale)),
        ])
      ),
      mergeMap(([getStatesRequest, endpoint, currentLabels, locale]) => {
        return this.http
          .get(
            `${this.statesUrl(endpoint)}${
              getStatesRequest.country
            }?userLanguage=en-US`
          )
          .pipe(
            map((statesData: any) => {
              const labels: any = currentLabels?.labels;
              const countryCMS =
                labels?.stellar?.country[getStatesRequest.country];
              const language = locale?.substring(0, 2);
              // If language is not English or states API does not return expected with 200
              //const subdivisions = statesData?.countries[0]?.subdivisions
              // if(!statesData?.countries[0]?.subdivisions || statesData?.countries[0]?.subdivisions?.length === 0) {
              //     let states!: States[];
              //     const subdivisions = countryCMS && countryCMS.states ? (statesData?.countries[0]?.subdivisions || getCountryConfig(getStatesRequest.country)?.states) : [];
              //     if(subdivisions) {
              //         states = this.mapStatesData(subdivisions, countryCMS);
              //     }
              //     statesData.countries[0].subdivisions = states || subdivisions;
              // }

              const subdivisions =
                countryCMS && !!countryCMS.states ? countryCMS.states : null;
              // let states!: States[];
              if (subdivisions) {
                statesData.countries[0].subdivisions = this.mapStatesData(
                  subdivisions,
                  countryCMS
                );
              }
              // statesData.countries[0].subdivisions = states || subdivisions;

              statesData = this.alphabetizeStateNames(statesData);
              return GetStatesSuccess({ statesResponse: statesData });
            }),
            catchError((error) => {
              const labels: any = currentLabels?.labels;
              const countriesCMS =
                labels?.stellar?.country[getStatesRequest.country];
              const defaultStates =
                getCountryConfig(getStatesRequest.country).states || [];
              return of({
                type: StatesActions.getStatesFailed,
                payload: {
                  error,
                  states: this.mapStatesData(defaultStates, countriesCMS),
                },
              });
            })
          );
      })
    )
  );

  alphabetizeStateNames(country: any): any {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const sortedStates = country.countries[0].subdivisions.sort(
      (a: any, b: any) => collator.compare(a.name, b.name)
    );
    country.countries[0].subdivisions = sortedStates;
    return country;
  }

  mapStatesData(states: States[], countryCMS: CountryCMS) {
    if (states && states.length > 0 && countryCMS?.states) {
      states = states.map((subDiv: States) => {
        const stateName = countryCMS?.states?.[subDiv.code];
        if (stateName) {
          subDiv = { ...subDiv, name: stateName };
        }
        return subDiv;
      });
      return states;
    }
    return states || [];
  }
}
